import type { Environment, PayframeFields, PayframeMountOptions, TokenizeResult } from './types'

/** Tokenization-only "payframes" — secure iframes mounted into the host
 *  page that capture card details and hand back opaque tokens. The
 *  underlying provider is intentionally internal; embedders interact
 *  with `pay.payframes` and never need to know about it. Field config
 *  matches donor-experience's AddOrEditCreditCard.vue (PFPT card-number,
 *  UUID CVV, PERSISTENT storage). */
export async function mountPayframes(
  vaultId: string,
  environment: Environment,
  opts: PayframeMountOptions,
  fraudSessionId?: string,
): Promise<PayframeFields> {
  const { loadVGSCollect } = await import('@vgs/collect-js')
  const collect = await loadVGSCollect({
    vaultId,
    environment: environment === 'live' ? 'live' : 'sandbox',
    version: '2.14.0',
  })
  const form = (await (collect as unknown as { init(): Promise<VgsFormHandle> }).init()) as VgsFormHandle

  // Style the inputs inside the payframes. Pattern + property casing
  // mirrors donor-experience's AddOrEditCreditCard/NewCreditCard
  // configs (kebab-case, top-level `background: transparent`, no iframe-
  // body styling) which is the empirically-known working VGS Collect
  // surface.
  //
  // Embedders can either pass `opts.style` (last-write-wins override) or
  // set CSS variables on `<html>` for the SDK to probe — both colors
  // here fall back to the host page's resolved body text color, so the
  // fields adapt to light vs dark themes without any explicit handling.
  const probeVar = (cssVar: string, fallback: string): string => {
    if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback
    const fromRoot = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
    return fromRoot || fallback
  }
  const probeComputed = (selector: 'color' | 'background-color', fallback: string): string => {
    if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback
    // Walk up from <body> looking for a non-transparent background — the
    // closest real surface color is what the wrapper "shows through" to.
    if (selector === 'background-color') {
      let node: Element | null = document.body
      while (node) {
        const bg = getComputedStyle(node).backgroundColor
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') return bg
        node = node.parentElement
      }
      return fallback
    }
    return getComputedStyle(document.body).color || fallback
  }
  const textColor = probeVar('--wegive-pay-color', probeComputed('color', '#111827'))
  const placeholderColor = probeVar('--wegive-pay-placeholder', textColor)
  // VGS Collect paints the iframe body with a default white. We can't
  // reach it from host CSS — but we CAN pass a `body` selector inside
  // the field css object, and VGS scopes it to the iframe's own body.
  // Probing the host's resolved background means the iframe matches
  // whatever surface it's sitting on, in light or dark mode, without
  // the embedder having to specify it.
  const bodyBg = probeVar('--wegive-pay-bg', probeComputed('background-color', '#ffffff'))

  const css = {
    'width': '100%',
    'height': '100%',
    'padding': '0 12px',
    'box-sizing': 'border-box',
    'background': 'transparent',
    'font-family': 'inherit',
    'font-size': '16px',
    'color': textColor,
    '&::placeholder': {
      'color': placeholderColor,
      'opacity': 0.55,
    },
    // Browser autofill paints a 1000px inset shadow onto the input — kill
    // it so saved cards don't flash a yellow/blue box that masks our bg.
    '&:-webkit-autofill': {
      '-webkit-box-shadow': `0 0 0 1000px ${bodyBg} inset !important`,
      '-webkit-text-fill-color': `${textColor} !important`,
    },
    // Top-level `body`/`html` selectors are scoped to the iframe document
    // by VGS Collect's CSS engine — this is how we paint over the default
    // white iframe body in dark mode.
    'body': {
      'margin': 0,
      'padding': 0,
      'background': bodyBg,
      'color': textColor,
    },
    'html': {
      'background': bodyBg,
    },
    ...(opts.style ?? {}),
  }

  // Resolve the host mount points up-front so `destroy()` can reclaim them
  // (VGS Collect ships no explicit teardown API — clearing the wrapper
  // elements removes the cross-origin iframes it injected).
  const mountPoints = [opts.cardNumber, opts.cardExpiry, opts.cardCvv].map(resolveElement)

  const state = { cc_number: false, cc_exp: false, cc_cvv: false }
  const onState = (name: keyof typeof state) => (s: { isValid?: boolean } | undefined) => {
    state[name] = !!s?.isValid
  }

  form.field(resolveSelector(opts.cardNumber), {
    name: 'cc_number',
    type: 'card-number',
    autoComplete: 'cc-number',
    placeholder: '1234 1234 1234 1234',
    validations: ['required', 'validCardNumber'],
    tokenization: { format: 'PFPT', storage: 'PERSISTENT' },
    showCardIcon: true,
    css,
    onUpdate: onState('cc_number'),
  })
  form.field(resolveSelector(opts.cardExpiry), {
    name: 'cc_exp',
    type: 'card-expiration-date',
    autoComplete: 'cc-exp',
    placeholder: 'MM / YY',
    yearLength: '2',
    validations: ['required', 'validCardExpirationDate'],
    tokenization: false,
    css,
    onUpdate: onState('cc_exp'),
  })
  form.field(resolveSelector(opts.cardCvv), {
    name: 'cc_cvv',
    type: 'card-security-code',
    autoComplete: 'cc-csc',
    placeholder: '•••',
    validations: ['required', 'validCardSecurityCode'],
    tokenization: { format: 'UUID', storage: 'PERSISTENT' },
    css,
    onUpdate: onState('cc_cvv'),
  })

  return {
    async ready() {
      // VGS attaches iframes synchronously after init; a microtask wait is enough.
      await Promise.resolve()
    },
    isValid() {
      return state.cc_number && state.cc_exp && state.cc_cvv
    },
    tokenize(): Promise<TokenizeResult> {
      return new Promise((resolve, reject) => {
        form.tokenize(
          (_status, data) => {
            const number = data.cc_number
            const exp = data.cc_exp
            const cvv = data.cc_cvv
            if (!number || !exp || !cvv) {
              reject(new Error('Tokenization returned no value for one or more fields'))
              return
            }
            resolve({
              cardNumber: number,
              cardExpiration: exp.replaceAll(' ', '').replaceAll('/', ''),
              cardCvv: cvv,
              ...(fraudSessionId ? { fraudSessionId } : {}),
            })
          },
          (errors) => reject(toError(errors)),
        )
      })
    },
    destroy() {
      // VGS Collect doesn't expose explicit teardown, so honor the documented
      // "tear down iframes" contract by clearing the wrapper elements we
      // mounted into — the injected cross-origin iframes are children of
      // these nodes, so emptying them removes the payframes from the DOM.
      for (const el of mountPoints) {
        if (el) el.innerHTML = ''
      }
    },
  }
}

interface VgsFormHandle {
  field: (selector: string | HTMLElement, config: Record<string, unknown>) => void
  tokenize: (
    onSuccess: (status: unknown, data: Record<string, string>) => void,
    onError: (errors: unknown) => void,
  ) => void
}

function resolveSelector(target: string | HTMLElement): string | HTMLElement {
  return target
}

/** Resolve a mount target to a concrete element, or null if it can't be
 *  found (SSR, or a selector that matches nothing). Used only for teardown. */
function resolveElement(target: string | HTMLElement): HTMLElement | null {
  if (typeof target !== 'string') return target
  if (typeof window === 'undefined') return null
  return document.querySelector<HTMLElement>(target)
}

function toError(errors: unknown): Error {
  if (errors instanceof Error) return errors
  if (typeof errors === 'string') return new Error(errors)
  return new Error('Card details are invalid')
}
